import React, { useState } from 'react'
import { nanoid } from 'nanoid'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import arrayMove from 'array-move'

import {
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Modal,
  TextField,
} from '@material-ui/core'
import { blueGrey } from '@material-ui/core/colors'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Clear as ClearIcon,
  DeleteForever as DeleteForeverIcon,
  FileCopy as FileCopyIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@material-ui/icons'

import Photo from './Photo'
import {
  Group as GroupType,
  Photo as PhotoType,
  WatermarkInfo,
} from '../../utils/Types'
import { paddingLeft } from '../../utils/methods'
import { useSelector } from 'react-redux'
import { AppState } from '../../redux/store'

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
    },
    header: {
      height: '58px',
      background: blueGrey[800],
      borderRadius: '10px',
      marginBottom: '10px',
    },
    releaseGroup: {
      display: 'inline-table',
      verticalAlign: 'top',
      marginLeft: '5px',
    },
    releaseGroupPlaceholder: {
      display: 'inline-block',
      width: '63px',
    },
    groupName: {
      margin: '10px 15px 0 10px',
    },
    groupNameError: {
      '& div': {
        '& fieldset': {
          borderWidth: '3px',
        },
      },
    },
    checkbox: {
      marginTop: '9px',
      color: 'white',
    },
    collapse: {
      display: 'inline-table',
      verticalAlign: 'top',
      float: 'right',
    },
    enlargeContainer: {
      width: '100%',
      textAlign: 'center',
      '&:focus': {
        outline: 'none',
      },
    },
    enlargeBody: {
      position: 'relative',
      display: 'table-cell',
      '&:focus': {
        outline: 'none',
      },
    },
    enlargeImg: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    enlargeWatermark: {
      position: 'absolute',
    },
  })
)

const ReleaseGroupButton = ({
  isTop,
  className,
  onClick,
}: {
  isTop: boolean
  className: string
  onClick: () => void
}) => {
  if (isTop) {
    return <div className={className} />
  }

  return (
    <IconButton
      aria-label="delete"
      className={className}
      onClick={onClick}
      title="グループ解除"
    >
      <ClearIcon fontSize="large" color="secondary" />
    </IconButton>
  )
}

type GroupBarProps = {
  isTop: boolean
  groupNameDefault: string
  isHeadBarcode: boolean
  isError: boolean
  collapse: boolean
  onReleaseGroup: () => void
  onGroupNameBlur: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  onChangeHeadBarcode: () => void
  onChangeCollapse: () => void
  onDeleteGroup: () => void
  onDuplicateGroup: () => void
}
const GroupBar: React.FC<GroupBarProps> = (props: GroupBarProps) => {
  const classes = useStyles()

  const [groupName, setGroupName] = useState(props.groupNameDefault)

  const onGroupNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGroupName(event.target.value.replace(/[¥¥/:?*"<>|]/g, ''))
  }

  return (
    <div className={classes.header}>
      <ReleaseGroupButton
        isTop={props.isTop}
        className={
          props.isTop ? classes.releaseGroupPlaceholder : classes.releaseGroup
        }
        onClick={props.onReleaseGroup}
      />
      <TextField
        error={props.isError}
        variant="outlined"
        size="small"
        label="グループ名"
        className={
          classes.groupName +
          (props.isError ? ' ' + classes.groupNameError : '')
        }
        value={groupName}
        onChange={onGroupNameChange}
        onBlur={props.onGroupNameBlur}
      />
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={props.isHeadBarcode}
            onChange={props.onChangeHeadBarcode}
          />
        }
        label="先頭バーコード"
        className={classes.checkbox}
      />
      <IconButton className={classes.collapse} onClick={props.onChangeCollapse}>
        {props.collapse ? (
          <KeyboardArrowUpIcon fontSize="large" />
        ) : (
          <KeyboardArrowDownIcon fontSize="large" />
        )}
      </IconButton>
      <IconButton
        aria-label="delete"
        title="グループ削除"
        className={classes.collapse}
        onClick={props.onDeleteGroup}
      >
        <DeleteForeverIcon fontSize="large" color="secondary" />
      </IconButton>
      <IconButton
        aria-label="duplicate"
        title="グループ複製"
        className={classes.collapse}
        onClick={props.onDuplicateGroup}
      >
        <FileCopyIcon fontSize="large" color="primary" />
      </IconButton>
    </div>
  )
}

type SortablePhotoProps = {
  photo: PhotoType
  src: string
  onEnlargePhoto: (photo: PhotoType) => void
  onSplitGroup: (photoId: string) => void
  onDuplicatePhoto: (photoId: string) => void
  onDeletePhoto: (photoId: string) => void
}
const SortablePhoto = SortableElement((props: SortablePhotoProps) => {
  return (
    <Photo
      key={props.photo.id}
      photo={props.photo}
      src={props.src}
      onEnlargePhoto={props.onEnlargePhoto}
      onSplitGroup={props.onSplitGroup}
      onDuplicatePhoto={props.onDuplicatePhoto}
      onDeletePhoto={props.onDeletePhoto}
    />
  )
})

type SortablePhotosProps = {
  photos: PhotoType[]
  onEnlargePhoto: (photo: PhotoType) => void
  onSplitGroup: (photoId: string) => void
  onDuplicatePhoto: (photoId: string) => void
  onDeletePhoto: (photoId: string) => void
}
const SortablePhotos = SortableContainer((props: SortablePhotosProps) => {
  return (
    <div>
      {props.photos.map((photo, index) => {
        return (
          <SortablePhoto
            key={photo.id}
            index={index}
            photo={photo}
            src={photo.src}
            onEnlargePhoto={props.onEnlargePhoto}
            onSplitGroup={props.onSplitGroup}
            onDuplicatePhoto={props.onDuplicatePhoto}
            onDeletePhoto={props.onDeletePhoto}
          />
        )
      })}
    </div>
  )
})

const enlargeSize = 500
const getEnlargeStyle = (size: { width: number; height: number }) => {
  let width = size.width
  let height = size.height

  if (width <= height && enlargeSize < height) {
    width *= enlargeSize / height
    height = enlargeSize
  } else if (height < width && enlargeSize < width) {
    height *= enlargeSize / width
    width = enlargeSize
  }

  return {
    width,
    height,
  }
}

const getWatermarkStyle = (
  watermarkInfo: WatermarkInfo,
  previewSize: number
) => {
  let ratio = 0
  if (watermarkInfo.previewSize.width < watermarkInfo.previewSize.height) {
    ratio = previewSize / watermarkInfo.previewSize.height
  } else {
    ratio = previewSize / watermarkInfo.previewSize.width
  }

  return {
    opacity: watermarkInfo.opacity / 100,
    width: watermarkInfo.size.width * ratio,
    height: watermarkInfo.size.height * ratio,
    top: watermarkInfo.position.y * ratio,
    left: watermarkInfo.position.x * ratio,
  }
}

const EnlargeModal = ({
  isEnlarge,
  photo,
  watermarkInfo,
  onNext,
  onPrevious,
  onClose,
}: {
  isEnlarge: boolean
  photo: PhotoType | undefined
  watermarkInfo: WatermarkInfo
  onNext: () => void
  onPrevious: () => void
  onClose: () => void
}) => {
  const classes = useStyles()
  const photoSize = useSelector((state: AppState) => state.edit.photoSize)
  const enlargeStyle = getEnlargeStyle(photoSize)
  const watermarkStyle = getWatermarkStyle(watermarkInfo, 500)

  if (photo === undefined) {
    return <></>
  }

  return (
    <Modal
      open={isEnlarge}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClose={onClose}
    >
      <div className={classes.enlargeContainer}>
        <div style={{ display: 'table', margin: '50px auto 0 auto' }}>
          <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
            <IconButton color="default" title="previous" onClick={onPrevious}>
              <ArrowBackIosIcon fontSize="large" />
            </IconButton>
          </div>
          <div style={enlargeStyle} className={classes.enlargeBody}>
            <img src={photo.src} alt="" className={classes.enlargeImg} />
            {photo.name === '001' ? (
              <img
                style={watermarkStyle}
                className={classes.enlargeWatermark}
                src={watermarkInfo.src}
                alt=""
              />
            ) : (
              <></>
            )}
            <div
              style={{
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                position: 'absolute',
              }}
            />
          </div>
          <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
            <IconButton color="default" title="next" onClick={onNext}>
              <ArrowForwardIosIcon fontSize="large" />
            </IconButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}

type Props = {
  isTop: boolean
  group: GroupType
  onReleaseGroup: (groupId: string) => void
  onDuplicateGroup: (groupId: string, groupName: string) => void
  onDeleteGroup: (groupId: string, groupName: string) => void
  onSplitGroup: (groupId: string, photoId: string) => void
  onUpdateGroup: (newGroup: GroupType) => void
}

const Group: React.FC<Props> = (props: Props) => {
  const classes = useStyles()
  const [collapse, setCollapse] = useState(true)
  const [isEnlarge, setIsEnlarge] = useState(false)
  const [enlargePhoto, setEnlargePhoto] = useState<PhotoType | undefined>(
    undefined
  )

  const watermarkInfo = useSelector(
    (state: AppState) => state.edit.watermarkInfo
  )

  const group = props.group
  const releaseGroup = props.onReleaseGroup
  const duplicateGroup = props.onDuplicateGroup
  const deleteGroup = props.onDeleteGroup
  const splitGroup = props.onSplitGroup
  const updateGroup = props.onUpdateGroup

  const toggleHeadBarcode = (): void => {
    const newGroup = Object.assign({}, group)
    newGroup.isHeadBarcode = !newGroup.isHeadBarcode
    const offset = newGroup.isHeadBarcode ? 0 : 1
    newGroup.photos.forEach((photo, index) => {
      photo.name = paddingLeft(index + offset, '0', 3)
    })

    props.onUpdateGroup(newGroup)
  }

  const toggleCollapse = (): void => {
    setCollapse(!collapse)
  }

  const toggleEnlarge = (): void => {
    setIsEnlarge(!isEnlarge)
  }

  const onGroupNameBlurHandler = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (group.name !== event.target.value) {
      const newGroup = Object.assign({}, group)
      newGroup.name = event.target.value
      newGroup.isError = false

      props.onUpdateGroup(newGroup)
    }
  }

  const releaseGroupHandler = React.useCallback((): void => {
    releaseGroup(group.id)
  }, [group.id, releaseGroup])

  const duplicateGroupHandler = React.useCallback((): void => {
    duplicateGroup(group.id, group.name)
  }, [group.id, group.name, duplicateGroup])

  const deleteGroupHandler = React.useCallback((): void => {
    deleteGroup(group.id, group.name)
  }, [group.id, group.name, deleteGroup])

  const splitGroupHandler = React.useCallback(
    (photoId: string) => {
      splitGroup(group.id, photoId)
    },
    [group.id, splitGroup]
  )

  const duplicatePhoto = React.useCallback(
    (photoId: string) => {
      const newGroup = Object.assign({}, group)

      const photoIndex = newGroup.photos.findIndex(
        (photo) => photo.id === photoId
      )

      const id = nanoid(20)
      const dupPhoto = Object.assign({}, newGroup.photos[photoIndex])
      dupPhoto.id = id

      newGroup.photos = [
        ...newGroup.photos.slice(0, photoIndex + 1),
        dupPhoto,
        ...newGroup.photos.slice(photoIndex + 1),
      ]

      const offset = newGroup.isHeadBarcode ? 0 : 1
      newGroup.photos.forEach((photo, index) => {
        photo.name = paddingLeft(index + offset, '0', 3)
      })

      updateGroup(newGroup)
    },
    [group, updateGroup]
  )

  const deletePhoto = React.useCallback(
    (photoId: string) => {
      const newGroup = Object.assign({}, group)
      newGroup.photos = newGroup.photos.filter((photo) => photo.id !== photoId)

      const offset = newGroup.isHeadBarcode ? 0 : 1
      newGroup.photos.forEach((photo, index) => {
        photo.name = paddingLeft(index + offset, '0', 3)
      })

      updateGroup(newGroup)
    },
    [group, updateGroup]
  )

  const onEnlargePhoto = (photo: PhotoType) => {
    setEnlargePhoto(photo)
    toggleEnlarge()
  }

  const onEnlargeNext = () => {
    const nowIndex = group.photos.findIndex(
      (photo) => photo.id === enlargePhoto?.id
    )

    if (nowIndex < 0) {
      return
    }

    let nextIndex = nowIndex + 1
    if (group.photos.length <= nextIndex) {
      nextIndex = 0
    }

    setEnlargePhoto(group.photos[nextIndex])
  }

  const onEnlargePrevious = () => {
    const nowIndex = group.photos.findIndex(
      (photo) => photo.id === enlargePhoto?.id
    )

    if (nowIndex < 0) {
      return
    }

    let nextIndex = nowIndex - 1
    if (nextIndex < 0) {
      nextIndex = group.photos.length - 1
    }

    setEnlargePhoto(group.photos[nextIndex])
  }

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => {
    if (oldIndex === newIndex) {
      return
    }

    const newGroup = Object.assign({}, group)
    newGroup.photos = arrayMove(newGroup.photos, oldIndex, newIndex)

    const offset = newGroup.isHeadBarcode ? 0 : 1
    newGroup.photos.forEach((photo, index) => {
      photo.name = paddingLeft(index + offset, '0', 3)
    })

    props.onUpdateGroup(newGroup)
  }

  return (
    <div className={classes.root}>
      <GroupBar
        isTop={props.isTop}
        groupNameDefault={group.name}
        isHeadBarcode={group.isHeadBarcode}
        isError={group.isError}
        collapse={collapse}
        onReleaseGroup={releaseGroupHandler}
        onGroupNameBlur={onGroupNameBlurHandler}
        onChangeHeadBarcode={toggleHeadBarcode}
        onChangeCollapse={toggleCollapse}
        onDeleteGroup={deleteGroupHandler}
        onDuplicateGroup={duplicateGroupHandler}
      />
      <Collapse in={collapse}>
        <SortablePhotos
          axis="xy"
          distance={10}
          onSortEnd={onSortEnd}
          photos={group.photos}
          onEnlargePhoto={onEnlargePhoto}
          onSplitGroup={splitGroupHandler}
          onDuplicatePhoto={duplicatePhoto}
          onDeletePhoto={deletePhoto}
        />
      </Collapse>
      <EnlargeModal
        isEnlarge={isEnlarge}
        photo={enlargePhoto}
        watermarkInfo={watermarkInfo}
        onNext={onEnlargeNext}
        onPrevious={onEnlargePrevious}
        onClose={toggleEnlarge}
      />
    </div>
  )
}

const areEqual = (prevState: Props, nextState: Props): boolean => {
  return (
    prevState.group === nextState.group && prevState.isTop === nextState.isTop
  )
}

export default React.memo(Group, areEqual)
